<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from "vue";

const props = defineProps({
  icon: {
    type: String,
    required: true,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  actions: {
    type: Array,
    default: () => [],
  },
});

const active = ref(false);

const emit = defineEmits(["click"]);

const clicked = (e) => {
  emit("click", e);
  toggleMenu();
};

// State for menu visibility
const showMenu = ref(false);

// Toggle menu function
const toggleMenu = () => {
  if (!props.disabled) {
    showMenu.value = !showMenu.value;
    active.value = showMenu.value;
  }
};

// Emit selected action and close menu
const selectAction = (action) => {
  action?.trigger();
  showMenu.value = false;
  active.value = false
};

// Button class computation
const buttonClass = computed(() => {
  return active.value ? "ecv-call-button active" : "ecv-call-button";
});

// Position menu above the button
const button = ref(null);
const menuStyle = computed(() => {
  if (!button.value || !showMenu.value) return {};
  const { top, left, height, width } = button.value.getBoundingClientRect();
  return {
    position: "absolute",
    top: `${top + (height / 2)}px`, // Position above the button
    left: `${left + (width / 2)}px`,
  };
});

// Close menu when clicking outside
const handleClickOutside = (event) => {
  if (!button.value.contains(event.target)) {
    showMenu.value = false;
    active.value = false
  }
};

onMounted(() => {
  document.addEventListener("click", handleClickOutside);
});

onBeforeUnmount(() => {
  document.removeEventListener("click", handleClickOutside);
});
</script>

<template>
  <div>
    <!-- Button with click event to toggle menu -->
    <button
      :disabled="props.disabled"
      :class="buttonClass"
      @click="clicked"
      :style="{ opacity: props.disabled ? 0.5 : 1 }"
      ref="button"
    >
      <v-icon :icon="props.icon" aria-hidden="true" />
    </button>

    <!-- Context menu -->
    <div
      v-if="showMenu"
      class="ecv-call-button-context-menu"
      :style="menuStyle"
      @click.stop
    >
      <ul>
        <li v-for="action in props.actions" :key="action" @click="selectAction(action)">
          <v-icon :icon="action.icon" aria-hidden="true" />&nbsp;&nbsp;{{ action.name }}
        </li>
      </ul>
    </div>
  </div>
</template>

<style scoped>
.ecv-call-button {
  transition: background-color 0.2s;
  background: #6d6d6dba;
  border: none;
  color: white;
  font-size: 1.1rem;
  padding: 0.5rem;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  box-shadow: 2px 2px 3px 1px rgba(0, 0, 0, 0.35);
}

.ecv-call-button:hover {
  background: #8d8d8dba;
}

.ecv-call-button.active {
  background: #656bb5ad;
}

.ecv-call-button-context-menu {
  background: #000;
  border: 1px solid #ccc;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  border-radius: 4px;
  padding: 8px 0;
  width: 220px;
  z-index: 1000;

  -webkit-user-select: none; /* Safari */
  -moz-user-select: none;    /* Firefox */
  -ms-user-select: none;     /* Internet Explorer/Edge */
  user-select: none;         /* Standard syntax */
}

.ecv-call-button-context-menu ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.ecv-call-button-context-menu li {
  padding: 8px 16px;
  cursor: pointer;
}

.ecv-call-button-context-menu li:hover {
  background-color: #e4e4e470;
}
</style>
